import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { NexusApiService } from '@df/api/nexus/nexus-api.service';
import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { Events } from '@df/core/service/event';
import { Status } from '@df/core/status';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import { IAddToBasketData } from '@df/session/api/basket/basket';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { BasketService, IRemoveFromBasketRequestData, IRemoveFromBasketSuccessEventData } from '@df/session/api/basket/basket.service';
import { Customer } from '@df/session/api/customer';
import { CustomerModel } from '@df/session/api/customer.model';
import { IWishlistItemData, WishlistItem, wishlistItemFactory } from '@df/session/api/wishlist/wishlist-item';
import { Customer as CustomerInterfaces } from '@df/session/customer/customer.interfaces';
import { NSession } from '@df/session/df-session.config';
import { NeedLoginService } from '@df/session/need-login.service';
import compact from 'lodash-es/compact';
import flatten from 'lodash-es/flatten';
import forEach from 'lodash-es/forEach';
import isArray from 'lodash-es/isArray';
import isEqual from 'lodash-es/isEqual';
import omit from 'lodash-es/omit';
import uniq from 'lodash-es/uniq';
import { Subject, Subscription } from 'rxjs';
import { WishlistDataChangeEvent } from './wishlist-data-change-event';
import { ProductResource } from '@df/catalog/product/product.resource';

export const WISHLIST_ADD_SUCCESS_EVENT = 'wishlist.add';
export const WISHLIST_REMOVE_SUCCESS_EVENT = 'wishlist.remove';
export const WISHLIST_TOGGLE_SUCCESS_EVENT = 'wishlist.toggle.success';

export interface IWishlistAddEvent {
    product: number[];
}
export type IWishlistRemoveEvent = number[];

/**
 * Wishlist data coming from BE or kept in storage
 */
export interface IWishlistData {
    items: IWishlistItemData[];
}

/**
 * Single wishlist item information
 */
export interface IAddToWishlistData extends Omit<IAddToBasketData, 'qty' | 'id'> {
    product: number | number[];
}

export interface IWishlistSendRequest {
    message?: string;
    emails: string[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IWishlistSendResponse {}

export interface IWishlistShareRequest {
    sender_name?: string;
    recipient_name?: string;
    recipient_email: string;
    message: string;
}

export type IMoveFromBasketSuccessEventData = IRemoveFromBasketSuccessEventData;

export type IMoveFromBasketRequestData = IRemoveFromBasketRequestData;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IWishlistShareResponse {}

/**
 * Data interface for wishlist add request
 */
export type IResponseAddToWishlist = CustomerInterfaces.ILoadResponse;

/**
 * Data interface for moveFromBasket request
 */
export type IResponseMoveFromBasket = CustomerInterfaces.ILoadResponse;

/**
 * Data interface for wishlist add request
 */
export type IResponseRemoveFromWishlist = CustomerInterfaces.ILoadResponse;

declare module '@df/core/service/df-event' {
    interface IEventType {
        // wishlist data
        wishlistDataChange: DfEventConfig<'wishlistDataChange', WishlistDataChangeEvent, void>;
    }
}

/**
 * Wishlist model
 */
@Injectable({
    providedIn: 'root'
})
export class Wishlist {
    qty = 0;

    /**
     * Items
     */
    items: WishlistItem[] = [];

    /**
     * Customer subscription
     */
    customerSubscription?: Subscription;

    /**
     * Wishlist changes subject
     */
    readonly wishlistUpdateSubject = new Subject<void>();

    /**
     * Services
     */
    private basketService = inject(BasketService);
    private productResource = inject(ProductResource);

    /**
     * Wishlist data
     */
    private _data!: IWishlistData;

    private moduleConfig = inject(NSession.OPTIONS);

    /**
     * Auth service
     */
    constructor(
        private needLoginService: NeedLoginService,
        private location: Location,
        private events: Events,
        protected app: App,
        private router: Router,
        protected requestService: RequestService,
        private customer: Customer,
        private customerModel: CustomerModel,
        private localStorageService: LocalStorageService,
        private timeoutService: TimeoutService,
        private nexusApiService: NexusApiService,
        private dfEventService: DfEventService
    ) {
        this.register();

        if (this.customerSubscription) {
            this.customerSubscription.unsubscribe();
        }

        this.customerModel.obs.data.subscribe(() => this.mergeGuestWishlist());
        this.mergeGuestWishlist();
    }

    protected register() {
        this.customer.register(
            {
                id: 'wishlist',
                listener: 'wishlist',
                account: false
            },
            this
        );
    }

    /**
     * Wishlist data getter
     */
    get data(): IWishlistData {
        return this.isGuestWishlist() ? { items: this.guestItems } : this._data;
    }

    /**
     * Wishlist data settter
     */
    set data(data: IWishlistData) {
        if (!isEqual(data, this._data)) {
            this.dfEventService.dispatch({
                id: 'wishlistDataChange',
                data: new WishlistDataChangeEvent(this._data, data, this),
                action: event => {
                    this._data = event.after!;
                    this.onDataChange();
                }
            });
        }
    }

    /**
     * Data setter require by customer.provider
     */
    setData(data: IWishlistData) {
        this.data = data;
    }

    ///////////////////////////////////////////////////////
    //
    // EVENTS
    //
    ///////////////////////////////////////////////////////

    /**
     * Register observer
     * Observer function are called on basket update
     * @param  {() => void} callback
     */
    subscribe(...args): Subscription {
        return this.wishlistUpdateSubject.subscribe(...args);
    }

    /**
     * Callec on data changes to sync items
     */
    protected onDataChange(): void {
        const wishlistItems = this.getItems();

        const callback = () => {
            this.items = wishlistItems;
            this.qty = this.items.length;

            this.next();
        };

        if (wishlistItems.length && this.moduleConfig.wishlist.loadProducts) {
            Promise.all(
                [
                    this.productResource.loadAllIds(this.getProductIdsToLoad(wishlistItems), this.moduleConfig.wishlist.productVerbosity, {
                        safe: true
                    }),
                    this.app.resources.attribute.loadForProduct(2)
                ].filter(i => !!i) as Promise<any>[]
            ).then(() => callback());
        } else {
            callback();
        }
    }

    protected getItems(data: IWishlistData = this.data): WishlistItem[] {
        const itemsData = data?.items || [];

        // if we hold guest wishlist then add its items too if logged in
        if (this.allowGuestWishlist()) {
            this.guestItems
                .filter(guestItem => !itemsData.find(itemData => itemData.product === guestItem.product))
                .forEach(item => itemsData.push(item));
        }
        return itemsData.map(itemData => this.createItem(itemData));
    }

    createItem(data: IWishlistItemData): WishlistItem {
        return new wishlistItemFactory.instance(this, data);
    }

    protected getProductIdsToLoad(items: WishlistItem[]): number[] {
        const ids: number[] = [];

        items.forEach(item => {
            if (item.product && (item.product.parent.isConfigurable || item.product.parent.isBundle)) {
                ids.push(...item.product.parent.childrenIds);
            }

            ids.push(item.data.product);
        });

        return uniq(ids);
    }

    /**
     * Load wishlist products
     */
    async load(): Promise<void> {
        await this.customerModel.load();
        const ids = this.getProductIdsToLoad(this.getItems());
        if (ids.length) {
            await this.productResource.loadAllIds(ids, this.moduleConfig.wishlist.productVerbosity, { safe: true });
        }
    }

    /**
     * Notifies about data changes
     */
    next(): void {
        this.wishlistUpdateSubject.next();
        this.events.broadcast('customer.wishlist.update');
    }

    ///////////////////////////////////////////////////////
    //
    // MANIPULATION
    //
    ///////////////////////////////////////////////////////

    /**
     * Toggles product(s) in wishlist
     *
     * @param {object} status
     * @param {number | number[]} productId
     * @param {bool} toState Optional parameter of desired state to toggle to (if not set then opposition of current state)
     */
    toggle(
        status: Status | undefined,
        source: number | WishlistItem,
        toState?: boolean,
        onSuccess?: IRequestCallback
    ): Promise<IResponseAddToWishlist> | undefined {
        const items = typeof source === 'number' ? this.getItemsByProduct(source) || [] : [source];
        const productId = typeof source === 'number' ? source : source?.data.product;

        if (toState === undefined) {
            toState = !items.length;
        }

        // bundle promises for all ids
        let promise: Promise<IResponseAddToWishlist> | undefined;
        // only make change if status is different
        if (toState !== !!items.length) {
            // if is in wishlist then remove item
            promise =
                (toState
                    ? this.add(status, { product: productId }, onSuccess)
                    : this.remove(
                          status,
                          items.map(i => i.data.id),
                          onSuccess
                      )) || undefined;
        }

        const cb = (response?: any) => {
            this.events.broadcast(WISHLIST_TOGGLE_SUCCESS_EVENT, {
                productId,
                response: response ? response.data : null
            });
        };

        promise ? promise.then(cb) : cb();

        return promise;
    }

    /**
     * Adds item to wishlist
     */
    add(
        status: Status | undefined,
        request: IAddToWishlistData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<IResponseAddToWishlist> | void {
        if (this.isGuestWishlist()) {
            const products = Array.isArray(request.product) ? request.product : [request.product];

            this.addToGuestWishlist(products, request);

            if (onSuccess) {
                forEach(flatten(isArray(onSuccess) ? onSuccess : [onSuccess]), onSuccessFunction => {
                    if (onSuccessFunction) {
                        onSuccessFunction(products);
                    }
                });
            }
        } else {
            const path = this.router.url;
            this.needLoginService.needLogin(() => {
                // this.$state.current.preventDefault = true;
                const promise = this.sendAddRequest(status, request, onSuccess, onError);

                // go back to original location
                if (path !== this.router.url) {
                    this.location.back();
                }

                return promise;
            });
        }
    }

    private addToGuestWishlist(products: number[], request?: IAddToWishlistData): void {
        const productsAdded: number[] = [];

        products.forEach(product => {
            if (!this.getItemsByProduct(product)?.length) {
                this.guestItems = [
                    ...this.guestItems,
                    {
                        id: product,
                        product: <number>product,
                        added_at: new Date().toISOString(),
                        ...omit(request || {}, '@df/catalog/product/product')
                    }
                ];

                productsAdded.push(product);
            }
        });

        this.events.broadcast<IWishlistAddEvent>(WISHLIST_ADD_SUCCESS_EVENT, { product: compact(productsAdded.map(i => i)) });

        this.onDataChange();
    }

    private get guestItems(): IWishlistItemData[] {
        return this.localStorageService.get('guestWishlist') || [];
    }

    private set guestItems(items: IWishlistItemData[] | null) {
        this.localStorageService.set('guestWishlist', items);
    }

    moveFromBasket(
        status: Status | undefined,
        basketItems: BasketItem | BasketItem[],
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<IResponseMoveFromBasket> | void {
        const items = Array.isArray(basketItems) ? basketItems : [basketItems];
        const ids = items.map(i => i.id);
        if (this.isGuestWishlist()) {
            this.basketService.remove(
                status,
                ids,
                compact(
                    flatten([
                        () => {
                            const products = compact(items.map(i => i.product?.id));
                            this.addToGuestWishlist(products);
                        },
                        onSuccess
                    ])
                ),
                onError
            );
        } else {
            const path = this.router.url;
            this.needLoginService.needLogin(() => {
                const promise = this.sendMoveFromBasketRequest(status, items, onSuccess, onError);

                // go back to original location
                if (path !== this.router.url) {
                    this.location.back();
                }

                return promise;
            });
        }
    }

    private sendMoveFromBasketRequest(
        status: Status | undefined,
        items: BasketItem[],
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<IResponseAddToWishlist> {
        const data: IMoveFromBasketRequestData = {
            ids: items.map(i => i.id)
        };
        return this.requestService.send(
            status,
            () => this.nexusApiService.wishlist.moveFromBasket(data),
            compact(
                flatten([
                    () => {
                        this.events.broadcast(WISHLIST_ADD_SUCCESS_EVENT, { product: compact(items.map(i => i.product?.id)) });
                    },
                    onSuccess
                ])
            ),
            onError
        );
    }

    /**
     * Sends add to wishlist request
     */
    private sendAddRequest(
        status: Status | undefined,
        data: IAddToWishlistData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback,
        event = true
    ): Promise<IResponseAddToWishlist> {
        // add to wishlist once logged in
        return this.requestService.send<IResponseAddToWishlist>(
            status,
            () => this.nexusApiService.wishlist.add(data),
            compact(
                flatten([
                    () => {
                        if (event) {
                            this.events.broadcast(WISHLIST_ADD_SUCCESS_EVENT, data);
                        }
                    },
                    onSuccess
                ])
            ),
            onError
        );
    }

    /**
     * Removes item from
     */
    remove(
        status: Status | undefined,
        itemId: number | number[],
        onSuccess?: IRequestCallback
    ): Promise<IResponseRemoveFromWishlist> | void {
        const ids = Array.isArray(itemId) ? itemId : [itemId];

        const productsIds = ids
            .map(id => {
                const item = this.getItemById(id);
                return item?.data?.product;
            })
            .filter(i => !!i) as number[];

        if (this.isGuestWishlist()) {
            this.guestItems = this.guestItems.filter(i => !ids.includes(i.id));

            this.app.events.broadcast<IWishlistRemoveEvent>(WISHLIST_REMOVE_SUCCESS_EVENT, productsIds);
            this.onDataChange();

            if (onSuccess) {
                forEach(flatten(isArray(onSuccess) ? onSuccess : [onSuccess]), onSuccessFunction => {
                    if (onSuccessFunction) {
                        onSuccessFunction(productsIds);
                    }
                });
            }
        } else {
            return this.requestService.send<IResponseRemoveFromWishlist>(
                status,
                () => this.nexusApiService.wishlist.remove({ ids }),
                () => {
                    this.app.events.broadcast<IWishlistRemoveEvent>(WISHLIST_REMOVE_SUCCESS_EVENT, productsIds);

                    if (onSuccess) {
                        forEach(flatten(isArray(onSuccess) ? onSuccess : [onSuccess]), onSuccessFunction => {
                            if (onSuccessFunction) {
                                onSuccessFunction(productsIds);
                            }
                        });
                    }
                }
            );
        }
    }

    ///////////////////////////////////////////////////////
    //
    // DATA
    //
    ///////////////////////////////////////////////////////

    /**
     * Retrieves wishlist item by its id
     */
    getItemById(id: number): WishlistItem | undefined {
        return this.items.find(wishlistItem => wishlistItem.data.id === id);
    }

    /**
     * Retieves wishlist item by product id
     */
    getItemsByProduct(id: number): WishlistItem[] | undefined {
        return this.items.filter(wishlistItem => wishlistItem.data.product === id);
    }

    //    /**
    //     * Checks if all requested products are in wishlist
    //     */
    //    has(productId: number): boolean {
    //        return !!this.getItemByProduct(productId);
    //    }

    ///////////////////////////////////////////////////////
    //
    // GUEST WISHLSIT
    //
    ///////////////////////////////////////////////////////

    /**
     * Checks if gues wishlist is allowed
     * (guest wishlist is kept in local storage)
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @return {boolean}
     */
    allowGuestWishlist(): boolean {
        return this.moduleConfig.wishlist.guest;
    }

    /**
     * Checks if wishlist is in guest mode now
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @return {boolean}
     */
    isGuestWishlist(): boolean {
        return this.allowGuestWishlist() && !this.customerModel.isLoggedIn;
    }

    /**
     * Checks if gues wishlist contains items
     */
    hasGuestModelItems(): boolean {
        return this.guestItems.length > 0;
    }

    mergeGuestWishlist(): void {
        this.timeoutService.setTimeout(() => {
            if (this.customer && this.customerModel.isLoggedIn && this.hasGuestModelItems()) {
                this.sendAddRequest(
                    undefined,
                    { product: this.guestItems.map(i => i.product) },
                    () => {
                        this.guestItems = null;
                    },
                    undefined,
                    false
                );
            }
        }, 100); // give other things some time, no rush
    }

    ///////////////////////////////////////////////////////
    //
    // SHARING
    //
    ///////////////////////////////////////////////////////
    /**
     * Checks if wishlist can be shared
     * @param  {wishslit model} wishlist
     * @return {bool}
     *
     * @todo  Make proper condition
     */
    // canShare(wishlistModel) {
    //     return !!wishlistModel.data.url;
    // }

    /**
     * Sends share wislist request
     */
    formShare(
        form: NgForm,
        status: Status | undefined,
        data: IWishlistShareRequest,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<IWishlistShareResponse> | void {
        return this.requestService.submitForm<IWishlistShareResponse>(
            form,
            status,
            () => this.nexusApiService.wishlist.share(data),
            onSuccess,
            onError
        );
    }

    /**
     * Sends wishlist to given email addressses
     */
    formSend(
        form: NgForm,
        status: Status | undefined,
        data: IWishlistSendRequest,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<IWishlistSendResponse> | void {
        return this.requestService.submitForm<IWishlistSendResponse>(
            form,
            status,
            {
                path: 'wishlist/send',
                data
            },
            onSuccess,
            onError
        );
    }
}

/**
 * Register service in customer
 */
declare module '@df/session/api/customer' {
    interface Customer {
        wishlist: Wishlist;
    }
}
